import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Avatar,
  Chip,
  Divider,
  Stack
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import palette from 'src/theme/palette';
import { Icon } from '@iconify/react';

const UserDetailsDialog = ({ open, onClose, user }) => {
  if (!user) return null;

  const StatusChip = ({ status }) => {
    const statusConfig = {
      pending: {
        color: '#92400e',
        icon: 'mdi:clock-outline',
        label: 'Pending',
        description: 'Project is pending and awaiting action',
        backgroundColor: '#fef3c7'
      },
      active: {
        color: '#065f46',
        icon: 'mdi:check-circle-outline',
        label: 'Active',
        description: 'Project is currently active',
        backgroundColor: '#d1fae5'
      }
    };

    const normalizedStatus = status?.toLowerCase().replace(/\s+/g, '');
    const config = statusConfig[normalizedStatus] || statusConfig.pending;

    return (
      <Chip
        icon={<Icon icon={config.icon} style={{ fontSize: 15, color: config.color }} />}
        label={config.label}
        variant="filled"
        title={config.description}
        sx={{
          color: config.color,
          backgroundColor: config.backgroundColor,
          fontFamily: 'poppinsMedium',
          fontSize: 12,
          height: 25,
          px: 1.2,
          borderRadius: '100px',
          '& .MuiChip-icon': {
            fontSize: '18px'
          }
        }}
      />
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '30vh',
          width: '400px'
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: palette.primary.main,
          height: 50,
          overflow: 'hidden'
        }}
      >
        <Typography sx={{ fontSize: 20, fontFamily: 'poppinsBold', color: palette.primary.white }}>
          User Details
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ color: '#fff' }} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2, position: 'relative' }}>
        {/* Basic Info */}
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
            <Avatar
              src={user.avatar}
              alt={user.firstname}
              sx={{ width: 100, height: 100, textTransform: 'capitalize' }}
            />
            <Box>
              <Typography
                sx={{
                  fontSize: 18,
                  fontFamily: 'poppinsSemiBold',
                  color: palette.primary.main,
                  textTransform: 'capitalize',
                  textAlign: 'center'
                }}
              >
                {user.firstname} {user.lastname}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  fontFamily: 'poppinsMedium',
                  color: palette.primary.titledark,
                  textAlign: 'center'
                }}
              >
                {user.email}
              </Typography>
            </Box>
          </Box>

          <Divider />

          {/* Role + Status */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography
                sx={{
                  fontSize: 18,
                  fontFamily: 'poppinsSemiBold',
                  color: palette.primary.main,
                  mb: 0.5
                }}
              >
                Role
              </Typography>
              <Chip
                label={user.role?.role_name}
                variant="filled"
                color="primary"
                sx={{
                  fontFamily: 'poppinsMedium',
                  fontSize: 12,
                  height: 25,
                  px: 1.2,
                  borderRadius: '100px'
                }}
              />
            </Box>
            <StatusChip status={user?.status} />
          </Box>

          {/* Contact */}
          <Box>
            <Typography
              sx={{
                fontSize: 18,
                fontFamily: 'poppinsSemiBold',
                color: palette.primary.main,
                mb: 0.5
              }}
            >
              Contact
            </Typography>
            <Typography
              sx={{
                fontSize: 14,
                fontFamily: 'poppinsMedium',
                color: palette.primary.titledark
              }}
            >
              {user.contact_no || 'N/A'}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default UserDetailsDialog;
